import { useState } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material'
import { ContentCopy } from '@mui/icons-material'
import { Keypair } from '@solana/web3.js'
import bs58 from 'bs58'

interface CollisionResult {
  address: string
  privateKey: string
  attempts: number
}

export default function Solana() {
  const [prefix, setPrefix] = useState('')
  const [suffix, setSuffix] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [results, setResults] = useState<CollisionResult[]>([])
  const [attempts, setAttempts] = useState(0)
  const [prefixError, setPrefixError] = useState('')
  const [suffixError, setSuffixError] = useState('')

  const generateKeyPair = () => {
    const keypair = Keypair.generate()
    const address = keypair.publicKey.toBase58()
    const privateKey = bs58.encode(keypair.secretKey)
    return { address, privateKey }
  }

  // Base58 character set (excluding 0, O, I, l)
  const BASE58_CHARSET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz'

  const validateBase58 = (input: string) => {
    for (let i = 0; i < input.length; i++) {
      if (!BASE58_CHARSET.includes(input[i])) {
        return false
      }
    }
    return true
  }

  const handlePrefixChange = (value: string) => {
    setPrefix(value)
    if (value && !validateBase58(value)) {
      setPrefixError('Prefix contains invalid characters. Base58 only allows: 1-9, A-Z, a-z (excluding 0, O, I, l)')
    } else {
      setPrefixError('')
    }
  }

  const handleSuffixChange = (value: string) => {
    setSuffix(value)
    if (value && !validateBase58(value)) {
      setSuffixError('Suffix contains invalid characters. Base58 only allows: 1-9, A-Z, a-z (excluding 0, O, I, l)')
    } else {
      setSuffixError('')
    }
  }

  const checkMatch = (address: string, targetPrefix: string, targetSuffix: string) => {
    const prefixMatch = !targetPrefix || address.startsWith(targetPrefix)
    const suffixMatch = !targetSuffix || address.endsWith(targetSuffix)
    return prefixMatch && suffixMatch
  }

  const startCollision = async () => {
    if (isSearching) return

    // Check input validation
    if (prefixError || suffixError) return
    if ((prefix && !validateBase58(prefix)) || (suffix && !validateBase58(suffix))) return

    setIsSearching(true)
    setAttempts(0)

    const search = () => {
      return new Promise<void>((resolve) => {
        const batchSize = 1000

        const searchBatch = (totalAttempts: number) => {
          let batchAttempts = 0

          for (let i = 0; i < batchSize; i++) {
            batchAttempts++
            const { address, privateKey } = generateKeyPair()

            if (checkMatch(address, prefix, suffix)) {
              const result: CollisionResult = {
                address,
                privateKey,
                attempts: totalAttempts + batchAttempts
              }
              setResults(prev => [result, ...prev])
              setAttempts(totalAttempts + batchAttempts)
              setIsSearching(false)
              resolve()
              return
            }
          }

          const newTotal = totalAttempts + batchAttempts
          setAttempts(newTotal)

          // Continue searching in next tick
          setTimeout(() => searchBatch(newTotal), 0)
        }

        searchBatch(0)
      })
    }

    await search()
  }

  const stopCollision = () => {
    setIsSearching(false)
  }

  const clearHistory = () => {
    setResults([])
    setAttempts(0)
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch((err) => {
      console.error('Copy failed:', err)
    })
  }

  return (
    <Box sx={{ p: 2, maxWidth: 800 }}>
      <Typography variant="h4" gutterBottom>
        Solana Address Collision
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Prefix"
              placeholder="Enter desired prefix (e.g., 'ABC')"
              value={prefix}
              onChange={(e) => handlePrefixChange(e.target.value)}
              disabled={isSearching}
              error={!!prefixError}
              helperText={prefixError}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Suffix"
              placeholder="Enter desired suffix (e.g., '123')"
              value={suffix}
              onChange={(e) => handleSuffixChange(e.target.value)}
              disabled={isSearching}
              error={!!suffixError}
              helperText={suffixError}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {!isSearching ? (
                <Button
                  variant="contained"
                  onClick={startCollision}
                  disabled={(!prefix && !suffix) || !!prefixError || !!suffixError}
                >
                  Start Collision
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  onClick={stopCollision}
                  startIcon={<CircularProgress size={16} />}
                >
                  Stop (Attempts: {attempts.toLocaleString()})
                </Button>
              )}

              {results.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={clearHistory}
                  disabled={isSearching}
                >
                  Clear History
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {results.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Collision History ({results.length})
          </Typography>

          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {results.map((result, index) => (
              <ListItem key={index} sx={{
                border: '1px solid #e0e0e0',
                mb: 1,
                borderRadius: 1,
                flexDirection: 'column',
                alignItems: 'flex-start'
              }}>
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" color="primary">
                      Address:
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Found at attempt {result.attempts.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1, mr: 1, wordBreak: 'break-all' }}>
                      {result.address}
                    </Typography>
                    <Tooltip title="Copy address">
                      <IconButton size="small" onClick={() => copyToClipboard(result.address)}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                    Private Key:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1, mr: 1, wordBreak: 'break-all' }}>
                      {result.privateKey}
                    </Typography>
                    <Tooltip title="Copy private key">
                      <IconButton size="small" onClick={() => copyToClipboard(result.privateKey)}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {!prefix && !suffix && !isSearching && results.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography variant="body1" color="textSecondary">
            Enter a prefix or suffix to start collision searching
          </Typography>
        </Paper>
      )}
    </Box>
  )
}